import { create } from 'zustand';
import { Notification } from '../types/notification';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isSSEConnected: boolean;
  
  // Actions
  connectSSE: () => void;
  disconnectSSE: () => void;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
}

let sseConnection: EventSource | null = null;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isSSEConnected: false,

  connectSSE: () => {
    if (sseConnection) {
      sseConnection.close();
    }

    const token = localStorage.getItem('auth-token');
    if (!token) return;

    try {
      // Connect to SSE endpoint with token as query parameter
      const url = `/api/notifications/stream?token=${encodeURIComponent(token)}`;
      sseConnection = new EventSource(url);

      sseConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            console.log('SSE connected');
            set({ isSSEConnected: true });
          } else {
            // It's a notification
            const notification = data as Notification;
            get().addNotification(notification);
            get().fetchUnreadCount();
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      sseConnection.onerror = (error) => {
        console.error('SSE error:', error);
        set({ isSSEConnected: false });
      };

      set({ isSSEConnected: true });
    } catch (error) {
      console.error('Failed to connect to SSE:', error);
      set({ isSSEConnected: false });
    }
  },

  disconnectSSE: () => {
    if (sseConnection) {
      sseConnection.close();
      sseConnection = null;
    }
    set({ isSSEConnected: false });
  },

  fetchNotifications: async () => {
    try {
      const { notificationService } = await import('../services/notification.service');
      const response = await notificationService.getNotifications();
      set({ notifications: response.data });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  },

  fetchUnreadCount: async () => {
    try {
      const { notificationService } = await import('../services/notification.service');
      const response = await notificationService.getUnreadCount();
      set({ unreadCount: response.data.count });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { notificationService } = await import('../services/notification.service');
      await notificationService.markAsRead(notificationId);
      set((state) => ({
        notifications: state.notifications.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      const { notificationService } = await import('../services/notification.service');
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  },

  deleteNotification: async (notificationId: string) => {
    try {
      const { notificationService } = await import('../services/notification.service');
      await notificationService.deleteNotification(notificationId);
      set((state) => ({
        notifications: state.notifications.filter(n => n.id !== notificationId),
        unreadCount: state.notifications.find(n => n.id === notificationId && !n.read)
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      }));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 50),
      unreadCount: state.unreadCount + 1,
    }));
  },
}));

